<template>
  <el-dialog :visible.sync="dialogVisible" :close-on-click-modal="false" width="980px" @close="onBatchModifyCancel">
    <div slot="title">
      <div class="modal-title">
        <h4>{{ title }}</h4>
        <span v-if="rows && rows.length">已选{{ rows.length }}个账号</span>
      </div>
    </div>

    <el-form class="ghost-button-styles" :inline="false" label-width="80px">
      <el-form-item label="统一设置">
        <div class="input-row">
          <el-select v-model="form.budgetType">
            <el-option label="起量预算" :value="1"></el-option>
          </el-select>
          <el-input class="budget-input" v-model="form.budgetValue" @keyup.enter.native="onBudgetValueConfirm" @blur="onBudgetValueConfirm">
            <template slot="append">
              <el-select :value="form.unitType">
                <el-option label="元" :value="1"></el-option>
              </el-select>
            </template>
          </el-input>
          <el-button type="text" @click="onGlobalUnableAction">全部设置为“关闭一键起量”</el-button>
        </div>
      </el-form-item>
    </el-form>

    <el-table
      ref="table"
      class="common-el-table-styles"
      border
      :row-class-name="getTableRowClassName"
      :data="rows"
      :height="320"
      width="100%"
    >
      <el-table-column prop="index" label="序号">
        <template slot-scope="{ $index }">
          <div>{{ $index + 1 }}</div>
        </template>
      </el-table-column>

      <el-table-column prop="planName" label="计划名称" show-overflow-tooltip></el-table-column>
      <el-table-column prop="planId" label="计划ID"></el-table-column>
      <el-table-column prop="mediaName" label="媒体"></el-table-column>
      <el-table-column label="当前预算">
        <template slot-scope="{ row }">
          <span>{{ row.budget === 0 ? '不限' : row.budget }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="paperUseAmount" label="当日消耗"></el-table-column>
      <el-table-column label="当前起量状态">
        <template slot-scope="{ row }">
          <span>{{ getAutoAcquisitionRowStatusText(row) }}</span>
        </template>
      </el-table-column>
      <el-table-column width="120">
        <template slot="header" slot-scope="scope">
          <span>修改后</span>
          <el-button style="margin-left: 10px" type="text" @click="removeAllBudgetValue(scope.row)">全部清除</el-button>
        </template>

        <template slot-scope="{ row }">
          <!-- 关闭、已完成、可预约、已预约 可修改预算 -->
          <EditActionPopover v-if="checkValidRow(row)" v-model="row.autoAcquisitionNewBudget">
            <el-button type="text">
              <span v-if="row.autoAcquisitionNewBudget > 0">{{ row.autoAcquisitionNewBudget }}</span>
              <span v-else> - </span>
            </el-button>
          </EditActionPopover>

          <!-- 已开启 只能关闭一键起量 -->
          <CloseActionPopover v-model="row.autoAcquisitionNewBudget" v-else-if="row.raiseStatus === AUTO_ACQUISITION_STATUS.ENABLE">
            <el-button type="text">
              <span v-if="row.autoAcquisitionNewBudget === 0">关闭</span>
              <span v-else> {{ row.autoAcquisitionNewBudget }} </span>
            </el-button>
          </CloseActionPopover>

          <span v-else>
            {{ row.autoAcquisitionBudget }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="140">
        <template slot-scope="{ $index, row }">
          <template v-if="submitFlag">
            <span v-if="row.autoAcquisitionResult" class="modify-status">
              <template v-if="row.autoAcquisitionResult.msg && row.autoAcquisitionResult.planId">
                <el-button :loading="row.retryLoading" type="text" @click="failedRowRetry(row)">重试</el-button>
                <span class="error"> <em class="el-icon-error"></em>修改失败</span>
                <el-tooltip popper-class="result-error-msg-popover" effect="dark" :content="row.autoAcquisitionResult.msg">
                  <em v-if="row.autoAcquisitionResult.msg" style="margin-left: 5px" class="el-icon-warning-outline"></em>
                </el-tooltip>
              </template>

              <!-- row.autoAcquisitionResult.planId 不存在，表示未修改 -->
              <template v-else-if="!row.autoAcquisitionResult.planId">
                <span> <em class="el-icon-error"></em>未修改</span>
              </template>

              <template v-else>
                <span class="success"> <em class="el-icon-circle-check"></em> 修改成功</span>
              </template>
            </span>
          </template>

          <el-button v-else type="text" @click="removeRow($index)">移除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <p class="error-msg" v-if="errorMsg">{{ errorMsg }}</p>

    <span slot="footer" class="dialog-footer">
      <el-button @click="onBatchModifyCancel">取 消</el-button>
      <el-button v-if="submitFlag" type="primary" @click="onBatchModifyCancel">知道了</el-button>
      <el-button v-else type="primary" :loading="confirmLoading" :disabled="rows.length === 0" @click="onBatchModifyConfirm">
        确 定
      </el-button>
    </span>
  </el-dialog>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { AUTO_ACQUISITION_STATUS } from './consts/index.js'
import EditActionPopover from './edit-popover.vue'
import CloseActionPopover from './close-popover.vue'
import { useBatchAutoAcquisition } from '../hooks/useBatchAutoAcquisition.js'
import { useBatchForm } from './hooks/useBatchForm.js'
import { useBatchSubmit } from './hooks/useBatchSubmit.js'

// eslint-disable-next-line
const props = defineProps({
  value: Boolean,
  title: String,
  rows: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['input', 'update:rows', 'refresh'])

const dialogVisible = computed({
  get() {
    return props.value
  },
  set(v) {
    emit('input', v)
  }
})

// Extract utility methods from the parent's hook
const { getAutoAcquisitionRowStatusText, checkValidRow, getTableRowClassName, tableValidate, buildBasicParams } = useBatchAutoAcquisition(
  null,
  emit
)

// Extract form related logic
const { form, onBudgetValueConfirm, removeAllBudgetValue, onGlobalUnableAction, removeRow, resetForm } = useBatchForm(
  props,
  emit,
  checkValidRow
)

// Extract submit related logic
const { submitFlag, errorMsg, confirmLoading, failedRowRetry, onBatchModifyConfirm, onBatchModifyCancel } = useBatchSubmit(props, emit, {
  getTableRowClassName,
  buildBasicParams,
  tableValidate,
  resetForm
})
</script>

<style lang="scss" scoped>
.input-row {
  display: flex;
  .el-button {
    margin-left: 10px;
  }

  .budget-input {
    margin-left: 10px;
  }
}

.budget-input {
  margin-left: 5px;
  ::v-deep .el-input-group__append {
    width: 70px;
  }
}

.modal-title {
  display: flex;
  align-items: center;
  h4 {
    font-size: 18px;
  }
  span {
    margin-left: 20px;
  }
}

.error-msg {
  color: red;
  font-size: 12px;
}

::v-deep .el-table {
  .disabled-row {
    color: #a4a4a4;
    cursor: not-allowed;
  }

  .modify-status {
    .error {
      color: red;
    }

    .success {
      color: green;
    }
  }
}
</style>
